import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Calendar,
  DollarSign,
  Bell,
  Activity,
  Filter,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  SimpleGrid,
  Box,
  Select,
  useToast,
  Badge,
  Progress,
  Divider,
  FormControl,
  FormLabel,
  Skeleton,
  SkeletonCircle,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts'
import { apiClient as api } from '../services/api'

// Types for analytics data
interface AnalyticsData {
  overview: {
    totalRevenue: number;
    totalBookings: number;
    totalUsers: number;
    totalNotifications: number;
    revenueGrowth: number;
    bookingGrowth: number;
    userGrowth: number;
    notificationGrowth: number;
  };
  revenueData: Array<{
    name: string;
    total: number;
    online: number;
    offline: number;
    mpesa: number;
    wallet: number;
    tigopesa: number;
    airtelmoney: number;
    halopesa: number;
    zenopay: number;
  }>;
  bookingStatusData: Array<{
    status: string;
    value: number;
    count: number;
    color: string;
  }>;
  routePerformance: Array<{
    name: string;
    revenue: number;
    bookings: number;
    completionRate: number;
    onTimeRate: number;
  }>;
  userGrowth: Array<{
    month: string;
    users: number;
    growth: number;
  }>;
  peakHours: Array<{
    hour: string;
    bookings: number;
  }>;
  notificationMetrics: Array<{
    type: string;
    sent: number;
    delivered: number;
    failed: number;
    rate: number;
  }>;
  notificationStats: Array<{
    type: string;
    sent: number;
    delivered: number;
    failed: number;
    rate: number;
  }>;
}

// Mock data for demonstration - in real app, this would come from your analytics services


const timeRanges = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '3m', label: 'Last 3 Months' },
  { value: '6m', label: 'Last 6 Months' },
  { value: '1y', label: 'Last Year' }
]

interface MetricCardProps {
  title: string
  value: string | number
  change: number
  icon: any
  color: string
  format?: 'number' | 'currency' | 'percentage'
  filterInfo?: string
}

function MetricCard({ title, value, change, icon: Icon, color, format = 'number', filterInfo }: MetricCardProps) {
  const formatValue = (val: string | number) => {
    if (format === 'currency') {
      return `TZS ${Number(val).toLocaleString()}`
    }
    if (format === 'percentage') {
      return `${Number(val).toFixed(1)}%`
    }
    return Number(val).toLocaleString()
  }

  const isPositive = change > 0
  const ChangeIcon = isPositive ? ArrowUpRight : ArrowDownRight

  return (
    <Card variant="glass" _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }} transition="all 0.3s">
      <CardBody p={6}>
        <Flex align="center" justify="space-between">
          <VStack align="start" spacing={2}>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" opacity={0.8}>
              {title}
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="gray.900">
              {formatValue(value)}
            </Text>
            <HStack spacing={1}>
              <ChangeIcon 
                size={16} 
                color={isPositive ? 'var(--chakra-colors-green-500)' : 'var(--chakra-colors-red-500)'} 
              />
              <Text 
                fontSize="sm" 
                fontWeight="medium" 
                color={isPositive ? 'green.500' : 'red.500'}
              >
                {Math.abs(change).toFixed(1)}% from last period
              </Text>
            </HStack>
            {filterInfo && (
              <Text fontSize="xs" color="gray.500" opacity={0.7}>
                {filterInfo}
              </Text>
            )}
          </VStack>
          <Box 
            p={4} 
            bg={`linear-gradient(135deg, ${color}, ${color}CC)`}
            borderRadius="2xl"
            boxShadow="lg"
          >
            <Icon className="w-7 h-7 text-white" />
          </Box>
        </Flex>
      </CardBody>
    </Card>
  )
}

function RevenueChart({ timeRange, route, metric, period, data }: {
  timeRange: string
  route: string
  metric: string
  period: string
  data?: AnalyticsData
}) {
  return (
    <Card variant="glass">
      <CardHeader>
        <Flex align="center" justify="space-between">
          <HStack spacing={3}>
            <Box p={2} bg="green.100" borderRadius="lg">
              <TrendingUp size={20} color="var(--chakra-colors-green-600)" />
            </Box>
            <VStack align="start" spacing={1}>
              <Heading size="md" color="gray.900">Revenue & Bookings Trend</Heading>
              <Text fontSize="sm" color="gray.600">
                {route === 'all' ? 'All Routes' : route.replace('-', ' - ')} • {metric} • {period}
              </Text>
            </VStack>
          </HStack>
          <HStack spacing={2}>
            <Select
              size="sm"
              w="120px"
              borderRadius="xl"
              borderColor="gray.300"
              value={metric}
            >
              <option value="revenue">Revenue</option>
              <option value="bookings">Bookings</option>
              <option value="passengers">Passengers</option>
            </Select>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data?.revenueData || []}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="bookingsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" stroke="#64748B" />
            <YAxis yAxisId="revenue" orientation="left" stroke="#64748B" />
            <YAxis yAxisId="bookings" orientation="right" stroke="#64748B" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} 
            />
            <Legend />
            <Area
              yAxisId="revenue"
              type="monotone"
              dataKey="total"
              stroke="#22C55E"
              fillOpacity={1}
              fill="url(#revenueGradient)"
              name="Revenue (TZS)"
            />
            <Area
              yAxisId="bookings"
              type="monotone"
              dataKey="online"
              stroke="#3B82F6"
              fillOpacity={1}
              fill="url(#bookingsGradient)"
              name="Online Revenue"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function BookingStatusChart({ data }: { data?: AnalyticsData }) {
  return (
    <Card variant="glass">
      <CardHeader>
        <HStack spacing={3}>
          <Box p={2} bg="blue.100" borderRadius="lg">
            <BarChart3 size={20} color="var(--chakra-colors-blue-600)" />
          </Box>
          <Heading size="md" color="gray.900">Booking Status Distribution</Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data?.bookingStatusData || []}
              cx="50%"
              cy="50%"
              outerRadius={100}
              dataKey="value"
              label={({ status, count }) => `${status}: ${count}`}
            >
              {(data?.bookingStatusData || []).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value: any, name: any, props: any) => [
                `${props.payload.count} bookings`,
                props.payload.status
              ]}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function RoutePerformanceChart({ timeRange, route, metric, period, data, routes }: {
  timeRange: string
  route: string
  metric: string
  period: string
  data?: AnalyticsData
  routes?: any[]
}) {
  // Helper function to get route name from ID
  const getRouteName = (routeId: string) => {
    if (routeId === 'all') return 'All Routes'
    const foundRoute = routes?.find((r: any) => r.id === routeId)
    return foundRoute ? `${foundRoute.name} (${foundRoute.startPoint} - ${foundRoute.endPoint})` : 'Unknown Route'
  }
  return (
    <Card variant="glass">
      <CardHeader>
        <Flex align="center" justify="space-between" w="full">
          <HStack spacing={3}>
            <Box p={2} bg="orange.100" borderRadius="lg">
              <Activity size={20} color="var(--chakra-colors-orange-600)" />
            </Box>
            <VStack align="start" spacing={1}>
              <Heading size="md" color="gray.900">Route Performance</Heading>
              <Text fontSize="sm" color="gray.600">
                {getRouteName(route)} • {metric} • {period}
              </Text>
            </VStack>
          </HStack>
          <HStack spacing={2}>
            <Select
              size="sm"
              w="120px"
              borderRadius="xl"
              borderColor="gray.300"
              value={metric}
            >
              <option value="trips">Trips</option>
              <option value="revenue">Revenue</option>
              <option value="passengers">Passengers</option>
              <option value="bookings">Bookings</option>
            </Select>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data?.routePerformance || []} layout="horizontal">
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis type="number" stroke="#64748B" />
            <YAxis dataKey="name" type="category" width={120} stroke="#64748B" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend />
            <Bar dataKey="totalBookings" fill="#3B82F6" name="Bookings" />
            <Bar dataKey="tripCompletionRate" fill="#22C55E" name="Completion Rate %" />
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function UserGrowthChart({ data }: { data?: AnalyticsData }) {
  return (
    <Card variant="glass">
      <CardHeader>
        <HStack spacing={3}>
          <Box p={2} bg="purple.100" borderRadius="lg">
            <Users size={20} color="var(--chakra-colors-purple-600)" />
          </Box>
          <Heading size="md" color="gray.900">User Growth</Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data?.userGrowth || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="month" stroke="#64748B" />
            <YAxis yAxisId="users" stroke="#64748B" />
            <YAxis yAxisId="newUsers" orientation="right" stroke="#64748B" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} 
            />
            <Legend />
            <Line
              yAxisId="users"
              type="monotone"
              dataKey="users"
              stroke="#8B5CF6"
              strokeWidth={3}
              name="Total Users"
              dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 6 }}
            />
            <Line
              yAxisId="newUsers"
              type="monotone"
              dataKey="newUsers"
              stroke="#F59E0B"
              strokeWidth={3}
              name="New Users"
              dot={{ fill: '#F59E0B', strokeWidth: 2, r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function PeakHoursChart({ timeRange, route, metric, period, data }: {
  timeRange: string
  route: string
  metric: string
  period: string
  data?: AnalyticsData
}) {
  return (
    <Card variant="glass">
      <CardHeader>
        <Flex align="center" justify="space-between" w="full">
          <HStack spacing={3}>
            <Box p={2} bg="teal.100" borderRadius="lg">
              <Calendar size={20} color="var(--chakra-colors-teal-600)" />
            </Box>
            <VStack align="start" spacing={1}>
              <Heading size="md" color="gray.900">Peak Booking Hours</Heading>
              <Text fontSize="sm" color="gray.600">
                {route === 'all' ? 'All Routes' : route.replace('-', ' - ')} • {period}
              </Text>
            </VStack>
          </HStack>
          <HStack spacing={2}>
            <Select
              size="sm"
              w="120px"
              borderRadius="xl"
              borderColor="gray.300"
              value={metric}
            >
              <option value="bookings">Bookings</option>
              <option value="revenue">Revenue</option>
              <option value="passengers">Passengers</option>
            </Select>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data?.peakHours || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="hour" stroke="#64748B" />
            <YAxis stroke="#64748B" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} 
            />
            <Bar dataKey="bookings" fill="#14B8A6" name="Bookings" />
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function NotificationMetrics({ data }: { data?: AnalyticsData }) {
  return (
    <Card variant="glass">
      <CardHeader>
        <HStack spacing={3}>
          <Box p={2} bg="red.100" borderRadius="lg">
            <Bell size={20} color="var(--chakra-colors-red-600)" />
          </Box>
          <Heading size="md" color="gray.900">Notification Performance</Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {(data?.notificationStats || []).map((stat, index) => (
            <Box key={index}>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="medium" color="gray.900">{stat.type}</Text>
                <Text fontSize="sm" color="gray.600">{stat.delivered}/{stat.sent} delivered</Text>
              </Flex>
              <Progress 
                value={stat.rate} 
                colorScheme={stat.rate > 95 ? 'green' : stat.rate > 90 ? 'yellow' : 'red'}
                size="lg"
                borderRadius="full"
              />
              <Flex justify="space-between" align="center" mt={2}>
                <Text fontSize="sm" color="gray.600">{stat.rate}% delivery rate</Text>
                <Badge 
                  colorScheme={stat.rate > 95 ? 'green' : stat.rate > 90 ? 'yellow' : 'red'}
                  borderRadius="full"
                  fontSize="xs"
                >
                  {stat.failed} failed
                </Badge>
              </Flex>
              {index < (data?.notificationStats || []).length - 1 && <Divider mt={4} />}
            </Box>
          ))}
        </VStack>
      </CardBody>
    </Card>
  )
}

export default function AnalyticsPage() {
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d')
  const [selectedRoute, setSelectedRoute] = useState('all')
  const [selectedMetric, setSelectedMetric] = useState('revenue')
  const [selectedPeriod, setSelectedPeriod] = useState('daily')
  const toast = useToast()
  const queryClient = useQueryClient()

  // Fetch real routes for filters
  const { data: routesData } = useQuery(
    'routes-for-analytics',
    () => api.get('/api/cms/routes?limit=100'), // Get all routes
    {
      retry: false,
    }
  )

  const routes = routesData?.data?.data || []

  // Helper function to get route name from ID
  const getRouteName = (routeId: string) => {
    if (routeId === 'all') return 'All Routes'
    const route = routes.find((r: any) => r.id === routeId)
    return route ? `${route.name} (${route.startPoint} - ${route.endPoint})` : 'Unknown Route'
  }

  // Filter options with real routes
  const routeOptions = [
    { value: 'all', label: 'All Routes' },
    ...routes.map((route: any) => ({
      value: route.id,
      label: `${route.name} (${route.startPoint} - ${route.endPoint})`
    }))
  ]

  const metricOptions = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'bookings', label: 'Bookings' },
    { value: 'passengers', label: 'Passengers' },
    { value: 'trips', label: 'Trips' }
  ]

  const periodOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ]

  // Memoized query key for React Query
  const analyticsQueryKey = useMemo(() => [
    'analytics',
    selectedTimeRange,
    selectedRoute,
    selectedMetric,
    selectedPeriod
  ], [selectedTimeRange, selectedRoute, selectedMetric, selectedPeriod])

  // Fetch analytics data with React Query for better caching and performance
  const fetchAnalyticsData = async () => {
    // Build query parameters
    const routeParam = selectedRoute !== 'all' ? `&route=${selectedRoute}` : ''
    
    try {
      const [statsResponse, revenueResponse, bookingsResponse, routePerformanceResponse, paymentBreakdownResponse, notificationsResponse] = await Promise.all([
        api.get('/api/cms/dashboard/statistics'),
        api.get(`/api/cms/dashboard/revenue?period=${selectedTimeRange}`),
        api.get(`/api/cms/dashboard/bookings?period=${selectedTimeRange}`),
        api.get(`/api/cms/dashboard/route-performance?period=${selectedTimeRange}&limit=50${routeParam}`),
        api.get(`/api/cms/dashboard/payment-breakdown?period=${selectedTimeRange}`),
        api.get(`/api/cms/dashboard/notifications?period=${selectedTimeRange}`)
      ])

      const stats = statsResponse.data.data
      const revenueData = revenueResponse.data.data
      const bookingsData = bookingsResponse.data.data
      const routeData = routePerformanceResponse.data.data
      const paymentData = paymentBreakdownResponse.data.data
      const notificationData = notificationsResponse.data.data

      return { stats, revenueData, bookingsData, routeData, paymentData, notificationData, revenueSummary: revenueResponse.data.summary }
    } catch (error) {
      // If primary range fails, try fallback ranges
      const fallbackRanges = ['30d', '7d', '3m', '6m', '1y'].filter(range => range !== selectedTimeRange)
      
      for (const fallbackRange of fallbackRanges) {
        try {
          const [statsResponse, revenueResponse, bookingsResponse, routePerformanceResponse, paymentBreakdownResponse] = await Promise.all([
            api.get('/api/cms/dashboard/statistics'),
            api.get(`/api/cms/dashboard/revenue?period=${fallbackRange}`),
            api.get(`/api/cms/dashboard/bookings?period=${fallbackRange}`),
            api.get(`/api/cms/dashboard/route-performance?period=${fallbackRange}&limit=50${routeParam}`),
            api.get(`/api/cms/dashboard/payment-breakdown?period=${fallbackRange}`)
          ])

          const stats = statsResponse.data.data
          const revenueData = revenueResponse.data.data
          const bookingsData = bookingsResponse.data.data
          const routeData = routePerformanceResponse.data.data
          const paymentData = paymentBreakdownResponse.data.data

          return { stats, revenueData, bookingsData, routeData, paymentData, revenueSummary: revenueResponse.data.summary }
        } catch (fallbackError) {
          continue // Try next fallback
        }
      }
      
      throw error // If all fallbacks fail, throw original error
    }
  }

  // React Query for analytics data with caching and error handling
  const { 
    data: analyticsData, 
    isLoading, 
    error, 
    refetch,
    isFetching 
  } = useQuery(
    analyticsQueryKey,
    async () => {
      const fetched = await fetchAnalyticsData()
      
      const stats = fetched?.stats ?? {}
      let revenueData = fetched?.revenueData ?? []
      let bookingsData = fetched?.bookingsData ?? []
      let { routeData } = fetched
      const revenueSummary = fetched.revenueSummary

      // Transform API data to match our interface
      const transformedData: AnalyticsData = {
        overview: {
          totalRevenue: stats?.revenue?.total || 0,
          totalBookings: stats?.bookings?.total || 0,
          totalUsers: stats?.passengers?.total || 0,
          totalNotifications: fetched?.notificationData?.reduce?.((sum: number, n: any) => sum + (n.sent || 0), 0) || 0,
          revenueGrowth: parseFloat(stats?.revenue?.change?.replace('%', '') || '0'),
          bookingGrowth: parseFloat(stats?.bookings?.change?.replace('%', '') || '0'),
          userGrowth: parseFloat(stats?.passengers?.change?.replace('%', '') || '0'),
          notificationGrowth: 0
        },
        revenueData: (revenueData || []).map((item: any) => ({
          name: item.name,
          total: item.total,
          online: item.online,
          offline: item.offline,
          mpesa: item.mpesa,
          wallet: item.wallet,
          tigopesa: item.tigopesa,
          airtelmoney: item.airtelmoney,
          halopesa: item.halopesa,
          zenopay: item.zenopay
        })),
        bookingStatusData: (() => {
          const completed = (bookingsData || []).reduce((sum: number, item: any) => sum + (item.completed || 0), 0)
          const confirmed = (bookingsData || []).reduce((sum: number, item: any) => sum + (item.confirmed || 0), 0)
          const pending = (bookingsData || []).reduce((sum: number, item: any) => sum + (item.pending || 0), 0)
          const cancelled = (bookingsData || []).reduce((sum: number, item: any) => sum + (item.cancelled || 0), 0)
          const total = completed + confirmed + pending + cancelled
          return [
            { status: 'Completed', value: total > 0 ? Math.round((completed / total) * 100) : 0, count: completed, color: '#22C55E' },
            { status: 'Confirmed', value: total > 0 ? Math.round((confirmed / total) * 100) : 0, count: confirmed, color: '#3B82F6' },
            { status: 'Pending', value: total > 0 ? Math.round((pending / total) * 100) : 0, count: pending, color: '#F59E0B' },
            { status: 'Cancelled', value: total > 0 ? Math.round((cancelled / total) * 100) : 0, count: cancelled, color: '#EF4444' }
          ].filter(s => s.count > 0)
        })(),
        routePerformance: (routeData || []).map((route: any) => ({
          name: route.name,
          revenue: route.revenue,
          totalBookings: route.totalBookings,
          tripCompletionRate: route.tripCompletionRate,
          onTimeRate: route.onTimeRate,
          completedTrips: route.completedTrips,
          trips: route.trips
        })),
        userGrowth: [], // Not available in current API
        peakHours: [], // Not available in current API
        notificationMetrics: [], // Not available in current API
        notificationStats: fetched?.notificationData ?? []
      }

      return { data: transformedData, actualTimeRange: revenueSummary?.period || selectedTimeRange }
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      onError: (error: any) => {
        console.error('Failed to fetch analytics data:', error)
        toast({
          title: 'Error',
          description: 'Failed to load analytics data. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    }
  )

  const handleRefresh = async () => {
    await refetch()
    toast({
      title: 'Analytics Refreshed',
      description: 'Latest analytics data has been loaded.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }

  const handleExport = () => {
    toast({
      title: 'Export Started',
      description: 'Analytics report is being generated.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    })
  }

  // Skeleton loading component for analytics cards
  const AnalyticsCardSkeleton = () => (
    <Card bg="white" shadow="sm">
      <CardBody>
        <VStack spacing={4} align="stretch">
          <HStack justify="space-between">
            <VStack align="start" spacing={2}>
              <Skeleton height="16px" width="120px" />
              <Skeleton height="32px" width="80px" />
            </VStack>
            <SkeletonCircle size="48px" />
          </HStack>
          <Skeleton height="20px" width="100%" />
        </VStack>
      </CardBody>
    </Card>
  )

  // Error component
  const ErrorComponent = ({ error, onRetry }: { error: any; onRetry: () => void }) => (
    <Alert status="error" borderRadius="md">
      <AlertIcon />
      <Box>
        <AlertTitle>Failed to load analytics data</AlertTitle>
        <AlertDescription>
          {error?.message || 'An unexpected error occurred while loading analytics data.'}
        </AlertDescription>
        <Button mt={3} size="sm" colorScheme="red" variant="outline" onClick={onRetry}>
          Try Again
        </Button>
      </Box>
    </Alert>
  )

  // Show error state
  if (error) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
          <VStack align={{ base: 'center', md: 'start' }} spacing={1} textAlign={{ base: 'center', md: 'left' }}>
            <Heading size={{ base: 'md', md: 'lg' }} color="gray.900">
              Analytics Dashboard
            </Heading>
          </VStack>
        </Flex>
        <ErrorComponent error={error} onRetry={() => refetch()} />
      </VStack>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={1} textAlign={{ base: 'center', md: 'left' }}>
          <Heading size={{ base: 'md', md: 'lg' }} color="gray.900">
            Analytics Dashboard
          </Heading>
          <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
            Daladala Smart operations Insights
          </Text>
        </VStack>
        <HStack spacing={3} w={{ base: 'full', md: 'auto' }} justify={{ base: 'center', md: 'flex-end' }} flexWrap="wrap">
          <Select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            w={{ base: 'full', sm: '180px' }}
            borderRadius="xl"
            borderColor="gray.300"
            size={{ base: 'sm', md: 'md' }}
          >
            {timeRanges.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </Select>
          <Button
            leftIcon={<RefreshCw size={16} />}
            variant="outline"
            borderRadius="xl"
            borderColor="gray.300"
            color="gray.600"
            _hover={{ bg: 'gray.50' }}
            onClick={handleRefresh}
            isLoading={isLoading}
            loadingText="Refreshing..."
            size={{ base: 'sm', md: 'md' }}
            w={{ base: 'full', sm: 'auto' }}
          >
            Refresh
          </Button>
          <Button
            leftIcon={<Download size={16} />}
            colorScheme="brand"
            borderRadius="xl"
            onClick={handleExport}
            size={{ base: 'sm', md: 'md' }}
            w={{ base: 'full', sm: 'auto' }}
          >
            Export Report
          </Button>
        </HStack>
      </Flex>

      {/* Advanced Filters */}
      <Card variant="glass">
        <CardBody p={6}>
          <VStack spacing={4} align="stretch">
            <HStack spacing={3}>
              <Box p={2} bg="brand.100" borderRadius="lg">
                <Filter size={20} color="var(--chakra-colors-brand-600)" />
              </Box>
              <Heading size="md" color="gray.900">Advanced Filters</Heading>
            </HStack>
            
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                  Time Range
                </FormLabel>
                <Select
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  borderRadius="xl"
                  borderColor="gray.300"
                  size="sm"
                >
                  {timeRanges.map((range) => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                  Route
                </FormLabel>
                <Select
                  value={selectedRoute}
                  onChange={(e) => setSelectedRoute(e.target.value)}
                  borderRadius="xl"
                  borderColor="gray.300"
                  size="sm"
                >
                  {routeOptions.map((route) => (
                    <option key={route.value} value={route.value}>
                      {route.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                  Metric
                </FormLabel>
                <Select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  borderRadius="xl"
                  borderColor="gray.300"
                  size="sm"
                >
                  {metricOptions.map((metric) => (
                    <option key={metric.value} value={metric.value}>
                      {metric.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                  Period
                </FormLabel>
                <Select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  borderRadius="xl"
                  borderColor="gray.300"
                  size="sm"
                >
                  {periodOptions.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>

            <Flex justify="flex-end" gap={3}>
              <Button
                variant="outline"
                size="sm"
                borderRadius="xl"
                borderColor="gray.300"
                color="gray.600"
                _hover={{ bg: 'gray.50' }}
                onClick={() => {
                  setSelectedTimeRange('30d')
                  setSelectedRoute('all')
                  setSelectedMetric('revenue')
                  setSelectedPeriod('daily')
                }}
              >
                Reset Filters
              </Button>
              <Button
                colorScheme="brand"
                size="sm"
                borderRadius="xl"
                onClick={handleRefresh}
                isLoading={isLoading}
                loadingText="Applying..."
              >
                Apply Filters
              </Button>
            </Flex>
          </VStack>
        </CardBody>
      </Card>

      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} minChildWidth="250px">
        {isLoading ? (
          // Show skeleton loading for all 4 cards
          Array.from({ length: 4 }).map((_, index) => (
            <AnalyticsCardSkeleton key={index} />
          ))
        ) : (
          <>
            <MetricCard
              title="Total Revenue"
              value={analyticsData?.data?.overview.totalRevenue || 0}
              change={analyticsData?.data?.overview.revenueGrowth || 0}
              icon={DollarSign}
              color="#22C55E"
              format="currency"
              filterInfo={`${analyticsData?.actualTimeRange || selectedTimeRange} • ${getRouteName(selectedRoute)}`}
            />
            <MetricCard
              title="Total Bookings"
              value={analyticsData?.data?.overview.totalBookings || 0}
              change={analyticsData?.data?.overview.bookingGrowth || 0}
              icon={Calendar}
              color="#3B82F6"
              filterInfo={`${analyticsData?.actualTimeRange || selectedTimeRange} • ${getRouteName(selectedRoute)}`}
            />
            <MetricCard
              title="Total Users"
              value={analyticsData?.data?.overview.totalUsers || 0}
              change={analyticsData?.data?.overview.userGrowth || 0}
              icon={Users}
              color="#8B5CF6"
              filterInfo={`${analyticsData?.actualTimeRange || selectedTimeRange} • ${getRouteName(selectedRoute)}`}
            />
            <MetricCard
              title="Notifications Sent"
              value={analyticsData?.data?.overview.totalNotifications || 0}
              change={analyticsData?.data?.overview.notificationGrowth || 0}
              icon={Bell}
              color="#EF4444"
              filterInfo={`${analyticsData?.actualTimeRange || selectedTimeRange} • ${getRouteName(selectedRoute)}`}
            />
          </>
        )}
      </SimpleGrid>

      {/* Charts Grid */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
        {isLoading ? (
          // Show skeleton loading for charts
          Array.from({ length: 4 }).map((_, index) => (
            <Card key={index} bg="white" shadow="sm" h="400px">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Skeleton height="24px" width="200px" />
                  <Skeleton height="300px" width="100%" />
                </VStack>
              </CardBody>
            </Card>
          ))
        ) : (
          <>
            <RevenueChart 
              timeRange={analyticsData?.actualTimeRange || selectedTimeRange}
              route={selectedRoute}
              metric={selectedMetric}
              period={selectedPeriod}
              data={analyticsData?.data}
            />
            <BookingStatusChart data={analyticsData?.data} />
            <RoutePerformanceChart 
              timeRange={analyticsData?.actualTimeRange || selectedTimeRange}
              route={selectedRoute}
              metric={selectedMetric}
              period={selectedPeriod}
              data={analyticsData?.data}
              routes={routes}
            />
            <UserGrowthChart data={analyticsData?.data} />
          </>
        )}
      </SimpleGrid>

      {/* Secondary Charts */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
        {isLoading ? (
          // Show skeleton loading for secondary charts
          Array.from({ length: 2 }).map((_, index) => (
            <Card key={index} bg="white" shadow="sm" h="350px">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Skeleton height="24px" width="180px" />
                  <Skeleton height="250px" width="100%" />
                </VStack>
              </CardBody>
            </Card>
          ))
        ) : (
          <>
            <PeakHoursChart 
              timeRange={analyticsData?.actualTimeRange || selectedTimeRange}
              route={selectedRoute}
              metric={selectedMetric}
              period={selectedPeriod}
              data={analyticsData?.data}
            />
            <NotificationMetrics data={analyticsData?.data} />
          </>
        )}
      </SimpleGrid>
    </VStack>
  )
}
